import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Palette, Pipette } from "lucide-react";

const PRESET_TARGETS = [
  { hex: "#7a1f2b", label: "Crimson" },
  { hex: "#1e4d7a", label: "Steel Blue" },
  { hex: "#2d5a27", label: "Forest Green" },
  { hex: "#5a3d7a", label: "Royal Purple" },
  { hex: "#4a4a4a", label: "Charcoal" },
  { hex: "#7a6a1f", label: "Bronze" },
];

// Lets a GM turn any owned (or shared Starter Pack) animation into a quick visually-distinct
// NPC variant — e.g. the Starter Pack: Human's brown leather armor into red — without rebuilding
// an animation from scratch. Always saves as a brand-new animation; the source is never mutated,
// so recoloring a Starter Pack never affects any other GM's copy of it.
export function RecolorAnimationModal({ open, onOpenChange, anim, onSaved }) {
  const [canvasEl, setCanvasEl] = useState(null); // callback ref — fires exactly when the
  // <canvas> attaches to the DOM, regardless of when the Dialog's portal actually mounts it
  // (a plain useRef can read stale/null here if the portal content mounts a render after `open`
  // flips true, which is what silently broke the eyedropper/preview draw before this fix).
  const [sourceHex, setSourceHex] = useState("#5a3e2b");
  const [targetHex, setTargetHex] = useState("#7a1f2b");
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const previewClip = anim?.clips?.idle ? "idle" : Object.keys(anim?.clips || {})[0];
  const frameSize = anim?.clips?.[previewClip]?.frame_size || 320;
  const sheetUrl = anim?.clips?.[previewClip]?.sprite_sheet_url;

  useEffect(() => {
    if (!open || !anim) return;
    setPreviewUrl(null);
    setName(`${anim.name} (Recolored)`);
  }, [open, anim]);

  useEffect(() => {
    if (!open || !canvasEl || !sheetUrl) return;
    const img = new window.Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const ctx = canvasEl.getContext("2d");
      ctx.clearRect(0, 0, canvasEl.width, canvasEl.height);
      ctx.drawImage(img, 0, 0, frameSize, frameSize, 0, 0, canvasEl.width, canvasEl.height);
    };
    img.onerror = () => {
      console.error("RecolorAnimationModal: failed to load sprite sheet image", sheetUrl);
      toast.error("Could not load the character preview — try reopening Recolor");
    };
    img.src = sheetUrl;
  }, [open, canvasEl, sheetUrl, frameSize]);

  useEffect(() => () => previewUrl && URL.revokeObjectURL(previewUrl), [previewUrl]);

  const pickSource = (e) => {
    if (!canvasEl) return;
    const rect = canvasEl.getBoundingClientRect();
    const x = Math.floor(((e.clientX - rect.left) / rect.width) * canvasEl.width);
    const y = Math.floor(((e.clientY - rect.top) / rect.height) * canvasEl.height);
    const ctx = canvasEl.getContext("2d");
    const [r, g, b, a] = ctx.getImageData(x, y, 1, 1).data;
    if (a === 0) return toast.info("That spot is transparent — tap the character itself");
    setSourceHex(`#${[r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("")}`);
  };

  const runPreview = async () => {
    if (!anim) return;
    setPreviewLoading(true);
    try {
      const { data } = await api.post(
        `/animations/${anim.id}/recolor-preview`,
        { source_hex: sourceHex, target_hex: targetHex },
        { responseType: "blob" }
      );
      setPreviewUrl((old) => {
        if (old) URL.revokeObjectURL(old);
        return URL.createObjectURL(data);
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Preview failed");
    } finally {
      setPreviewLoading(false);
    }
  };

  const save = async () => {
    if (!anim) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/animations/${anim.id}/recolor`, {
        source_hex: sourceHex, target_hex: targetHex, name: name.trim(),
      });
      toast.success(`"${data.name}" saved to your library`);
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      if (err.response?.status === 403) toast.error("The Animation Maker needs an active GM Pass subscription");
      else toast.error(err.response?.data?.detail || "Could not save the recolored animation");
    } finally {
      setSaving(false);
    }
  };

  if (!anim) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#151613] border-white/10 text-[#f1e0c5] max-w-lg" data-testid="recolor-animation-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <Palette className="w-5 h-5 text-[#D4AF37]" /> Recolor "{anim.name}"
          </DialogTitle>
          <DialogDescription className="text-[#f1e0c5]/50 text-xs flex items-center gap-1.5">
            <Pipette className="w-3 h-3 shrink-0" /> Tap the color you want to change below, then pick a new one — saves as a brand-new animation, the original is never touched.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-4 justify-center">
          <div className="space-y-1.5">
            <canvas
              ref={setCanvasEl}
              data-testid="recolor-source-canvas"
              width={160}
              height={160}
              onClick={pickSource}
              className="rounded-lg border border-white/10 bg-black/30 cursor-crosshair"
            />
            <p className="text-[9px] text-[#f1e0c5]/30 text-center">Tap to pick a color</p>
          </div>
          <div className="space-y-1.5">
            {previewLoading ? (
              <div className="w-40 h-40 rounded-lg border border-white/10 bg-black/30 flex items-center justify-center text-[10px] text-[#f1e0c5]/30">Rendering…</div>
            ) : previewUrl ? (
              <img data-testid="recolor-preview-image" src={previewUrl} alt="Recolor preview" className="w-40 h-40 rounded-lg border border-[#D4AF37]/40 bg-black/30 object-contain" />
            ) : (
              <div className="w-40 h-40 rounded-lg border border-white/10 bg-black/30 flex items-center justify-center text-[10px] text-[#f1e0c5]/30 text-center p-3">Click "Preview" to see the result</div>
            )}
            <p className="text-[9px] text-[#f1e0c5]/30 text-center">Preview</p>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] uppercase text-[#f1e0c5]/40">From</span>
            <input data-testid="recolor-source-color-input" type="color" value={sourceHex} onChange={(e) => setSourceHex(e.target.value)} className="w-8 h-8 rounded border border-white/10 bg-transparent" />
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] uppercase text-[#f1e0c5]/40">To</span>
            <input data-testid="recolor-target-color-input" type="color" value={targetHex} onChange={(e) => setTargetHex(e.target.value)} className="w-8 h-8 rounded border border-white/10 bg-transparent" />
          </div>
          <Button data-testid="recolor-preview-button" size="sm" variant="secondary" className="rounded-full text-xs ml-auto" onClick={runPreview} disabled={previewLoading}>
            {previewLoading ? "Rendering…" : "Preview"}
          </Button>
        </div>

        <div className="flex flex-wrap gap-1.5">
          {PRESET_TARGETS.map((p) => (
            <button
              key={p.hex}
              data-testid={`recolor-preset-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setTargetHex(p.hex)}
              title={p.label}
              style={{ backgroundColor: p.hex }}
              className={`w-6 h-6 rounded-full border-2 transition-colors duration-200 ${targetHex === p.hex ? "border-[#D4AF37]" : "border-white/20"}`}
            />
          ))}
        </div>

        <Input data-testid="recolor-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="New animation name" className="bg-black/30 border-white/10" />

        <Button data-testid="recolor-save-button" onClick={save} disabled={saving} className="w-full rounded-full bg-[#D90429] hover:bg-[#b8031f]">
          {saving ? "Saving…" : "Save as New Animation"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
